import { createClient, type AuthError, type Session, type SupabaseClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://qsenozfawbynwkmpxyjr.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFzZW5vemZhd2J5bndrbXB4eWpyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY5NDM5NjAsImV4cCI6MjA5MjUxOTk2MH0.Ti6p44hmOaZF8PBisEjrEU0NPGCc6yFGB4Bu02L_cHk';

export { SUPABASE_URL, SUPABASE_ANON_KEY };

const AUTH_REQUEST_TIMEOUT_MS = 8000;

let supabaseClient: SupabaseClient | null = null;

function createSupabaseClient(): SupabaseClient {
  const isBrowser = typeof window !== 'undefined';

  if (isBrowser) {
    clearStaleAuth();
  }

  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      autoRefreshToken: isBrowser && !__IS_PREVIEW__,
      persistSession: isBrowser,
      detectSessionInUrl: isBrowser && !__IS_PREVIEW__,
    },
  });
}

function getSupabaseClient(): SupabaseClient {
  if (!supabaseClient) {
    supabaseClient = createSupabaseClient();
  }
  return supabaseClient;
}

/**
 * Lazy Supabase client — createClient runs on first use, not at module import.
 * Prevents Readdy import/preview from blocking on auth recovery before React mounts.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getSupabaseClient();
    const value = Reflect.get(client, prop, client);
    return typeof value === 'function' ? value.bind(client) : value;
  },
});

/** Public portal routes where invalid-session cleanup should stay silent. */
export function isPublicPortalRoute(pathname?: string): boolean {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const prefix = __BASE_PATH__.replace(/\/$/, '');
  let route = path;
  if (prefix && route.startsWith(prefix)) {
    route = route.slice(prefix.length) || '/';
  }
  return route === '/' || route === '/login' || route.startsWith('/login/');
}

/**
 * Extract the project reference from the Supabase URL.
 * Works for both old and new URL formats.
 */
function getProjectRef(url: string): string {
  return url
    .replace('https://', '')
    .replace(/\.supabase\.co$/, '')
    .replace(/\.supabase\.in$/, '')
    .replace(/\.supabase\.net$/, '');
}

function isSupabaseAuthStorageKey(key: string, projectRef?: string): boolean {
  if (!key.startsWith('sb-') || !key.includes('auth-token')) return false;
  if (!projectRef) return true;
  return key.includes(projectRef);
}

/**
 * Remove stale Supabase auth tokens from OTHER projects only.
 * NEVER delete the current project's token — that causes instant logout.
 */
export function clearStaleAuth(): void {
  if (typeof window === 'undefined') return;

  try {
    const currentRef = getProjectRef(SUPABASE_URL);

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;

      if (!isSupabaseAuthStorageKey(key)) continue;

      if (key.includes(currentRef)) {
        continue;
      }

      console.log('[clearStaleAuth] Removing stale auth key from other project:', key);
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable — ignore
  }
}

/**
 * Force-clear persisted auth storage for the current Supabase project.
 * Used after invalid refresh token recovery or logout.
 */
export function clearLocalAuthStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    const currentRef = getProjectRef(SUPABASE_URL);

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (isSupabaseAuthStorageKey(key, currentRef)) {
        console.log('[clearLocalAuthStorage] Removing auth key:', key);
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable — ignore
  }
}

function collectAuthErrorText(error: unknown): string {
  if (!error) return '';
  const parts: string[] = [];

  if (typeof error === 'string') parts.push(error);
  if (error instanceof Error) {
    parts.push(error.name, error.message);
  }

  const obj = error as Record<string, unknown>;
  if (typeof obj.message === 'string') parts.push(obj.message);
  if (typeof obj.msg === 'string') parts.push(obj.msg);
  if (typeof obj.error_description === 'string') parts.push(obj.error_description);
  if (typeof obj.error === 'string') parts.push(obj.error);

  return parts.join(' ').toLowerCase();
}

/**
 * Check if an error is an invalid/expired refresh token error.
 * These happen when the session was revoked or the refresh token expired.
 */
export function isAuthRefreshError(error: unknown): boolean {
  const msg = collectAuthErrorText(error);
  if (!msg) return false;

  return (
    msg.includes('invalid refresh token') ||
    msg.includes('refresh token not found') ||
    msg.includes('token has expired') ||
    msg.includes('refresh token "refresh_token" not found') ||
    (msg.includes('refresh') && msg.includes('token') && msg.includes('not found'))
  );
}

/**
 * Handle an auth refresh error by clearing the local session only.
 * Uses scope: 'local' so we do NOT try to call the server with a bad token.
 */
export async function handleAuthRefreshError(error: unknown): Promise<boolean> {
  if (!isAuthRefreshError(error)) return false;
  console.log('[handleAuthRefreshError] Detected invalid refresh token. Clearing local session.');
  clearLocalAuthStorage();
  try {
    await Promise.race([
      supabase.auth.signOut({ scope: 'local' }),
      new Promise<void>((resolve) => setTimeout(resolve, 1500)),
    ]);
  } catch (signOutErr) {
    console.log('[handleAuthRefreshError] Local signOut also failed:', signOutErr);
  }
  return true;
}

type SafeGetSessionResult = {
  data: { session: Session | null };
  error: AuthError | null;
  timedOut?: boolean;
};

/**
 * Non-blocking session read with timeout and invalid-token cleanup.
 * Safe to call from useAuth/AuthGuard after React mount.
 */
export async function safeGetSession(): Promise<SafeGetSessionResult> {
  try {
    const result = await Promise.race([
      supabase.auth.getSession().then((value) => ({ kind: 'result' as const, value })),
      new Promise<{ kind: 'timeout' }>((resolve) => {
        setTimeout(() => resolve({ kind: 'timeout' }), AUTH_REQUEST_TIMEOUT_MS);
      }),
    ]);

    if (result.kind === 'timeout') {
      console.warn('[safeGetSession] timed out — continuing as signed out');
      return { data: { session: null }, error: null, timedOut: true };
    }

    const { data, error } = result.value;
    if (error && isAuthRefreshError(error)) {
      await handleAuthRefreshError(error);
      return { data: { session: null }, error: null };
    }

    return { data, error };
  } catch (err) {
    if (isAuthRefreshError(err)) {
      await handleAuthRefreshError(err);
      return { data: { session: null }, error: null };
    }
    console.error('[safeGetSession] unexpected error', err);
    return { data: { session: null }, error: err as AuthError };
  }
}
